import { usStateAbbreviation, normalizeUsState } from './stateRegion';

// Common English connectors that should stay lowercase inside corridor labels
// like "US-287 N and I-40 W" or "Route 66 to Phoenix" (markup #78). Compared
// case-insensitively against the bare token.
const LOWERCASE_CONNECTORS = new Set(['and', 'or', 'of', 'the', 'to', 'at', 'for', 'on', 'in']);

function titleCasePart(part) {
  if (!part) return '';
  if (LOWERCASE_CONNECTORS.has(part.toLowerCase())) return part.toLowerCase();
  if (/^[A-Za-z]+-?\d+/.test(part)) return part.toUpperCase();
  if (part.length <= 3 && /^[A-Za-z]+$/.test(part)) return part.toUpperCase();
  return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
}

/**
 * Display name for a booked guest, Title-Cased, falling back to "Anonymous".
 *
 * `fn_get_ri_booked_guests` returns `guest_name: ''` (with
 * `guest_name_is_fallback: true` and both `first_name`/`last_name` null) when the
 * PMS sync captured no name — 9 of 25 rows for Verde Ranch. Rendering that
 * straight left the Guest column blank, so the row read as broken rather than
 * as a party whose name simply isn't known.
 *
 * "Anonymous" matches what `rateSimulator.js` already substitutes, so the Trip
 * Economics table and this one agree.
 *
 * `guest_name_is_fallback: true` marks a placeholder the backend substituted for a
 * missing name, so it is treated as nameless even when `guest_name` is non-empty.
 *
 * @param {string|null|undefined} raw `guest_name` as the RPC returns it.
 * @param {boolean} [isFallback] `guest_name_is_fallback` from the same row.
 * @returns {string} Title-Cased name, or "Anonymous" when there is nothing to show.
 */
export function guestDisplayName(raw, isFallback) {
  if (isFallback === true) return 'Anonymous';
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return 'Anonymous';
  return s.toLowerCase().replace(/(?<![\p{L}\p{M}])\p{L}/gu, (c) => c.toUpperCase());
}

// Strips the "(guest home)" / "GUEST HOME" artifacts that the
// route_intelligence and v_booking_enriched columns ship with. Use for
// human-readable comma-separated labels (origin_label, home_location).
// Preserves a "US" country code: "Glendale, Arizona US Guest Home" → "Glendale, Arizona, US".
export function cleanLocationLabel(raw) {
  if (raw == null || raw === '') return '—';
  let s = String(raw).trim();
  if (!s) return '—';
  s = s.replace(/\s*\(\s*guest\s+home\s*\)\s*/gi, ' ');
  s = s.replace(/\bUS\s+GUEST\s+HOME\b/gi, ', US');
  s = s.replace(/\bGUEST\s+HOME\b/gi, ' ');
  s = s.replace(/\s+,/g, ',');
  s = s.replace(/,\s*,/g, ',');
  s = s.replace(/,\s*$/, '');
  s = s.replace(/\s+/g, ' ').trim();
  return s || '—';
}

// Normalize a trailing US country code to the dashboard convention "US"
// (markup #85). Origin/corridor labels already resolve to "US" via
// cleanLocationLabel / formatCorridorLabel, but fuel-station addresses ship
// straight from the provider with "..., USA" (e.g. "Cottonwood, AZ 86326,
// USA"). Only the trailing token is touched, so non-US addresses are left
// alone.
export function normalizeCountry(address) {
  if (address == null || address === '') return '—';
  const s = String(address).trim();
  if (!s) return '—';
  return s.replace(/,?\s*(?:USA|United States(?:\s+of\s+America)?)\.?\s*$/i, ', US');
}

// Expand a 2-letter US state abbreviation embedded in a fuel-station address to
// its full name (markup #85 follow-up), so the cheapest-gas table reads
// "Cottonwood, Arizona 86326, US" instead of "Cottonwood, AZ 86326, US" — the
// same full-state-name convention formatCorridorLabel and the origin labels
// use. Only a comma-preceded token that sits right before a ZIP, another
// comma, or end-of-string is considered, and only if it resolves to a real
// state — so the country code ("US"), highway tokens ("AZ-260"), and ordinary
// words are left untouched.
export function expandStateInAddress(address) {
  if (address == null) return address;
  return String(address).replace(
    /,(\s*)([A-Za-z]{2})\b(?=\s+\d{5}(?:-\d{4})?\b|\s*,|\s*$)/g,
    (match, space, code) => {
      const full = normalizeUsState(code);
      return full ? `,${space}${full}` : match;
    },
  );
}

// Display formatter for fuel-station addresses: spell out the state and
// normalize the trailing country code to "US".
export function formatStationAddress(address) {
  return normalizeCountry(expandStateInAddress(address));
}

// Title-case a single word, preserving highway-style tokens (US-95, I-40, SK-6),
// keeping common English connectors lowercase ("and", "or", "to", …) so
// "CA-99 S and I-40 E" doesn't auto-title-case "and" → "And" (markup #78), and
// recursing on slash-separated segments so "Hwy/sk-6" → "Hwy/SK-6" and
// "W/superstition" → "W/Superstition" instead of leaving the right side
// lowercase (markup #78 follow-up).
function smartCase(w) {
  if (!w) return '';
  // Rejoin slash-separated segments with " / " so "Hwy/sk-6" reads as
  // "Hwy / SK-6" — breathing room around the slash is easier to scan.
  if (w.includes('/')) return w.split('/').map(smartCase).join(' / ');
  if (LOWERCASE_CONNECTORS.has(w.toLowerCase())) return w.toLowerCase();
  // Case-insensitive on the highway-code regex so lowercase route prefixes
  // ("sk-6") still upper-case to "SK-6".
  if (/^[A-Za-z]+-?\d/.test(w)) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

// Common US street-suffix tokens used to detect where the street name ends
// and the city begins inside an address-style waypoint label.
const STREET_SUFFIXES = new Set([
  'AVE', 'AV', 'AVENUE',
  'ST', 'STREET',
  'RD', 'ROAD',
  'DR', 'DRIVE',
  'BLVD', 'BOULEVARD',
  'LN', 'LANE',
  'CT', 'COURT',
  'PL', 'PLACE',
  'WAY',
  'HWY', 'HIGHWAY',
  'PKWY', 'PARKWAY',
  'FWY', 'FREEWAY',
  'CIR', 'CIRCLE',
  'TRL', 'TRAIL',
  'TER', 'TERRACE',
  'SQ', 'SQUARE',
]);

// Per markup-comment #38, corridor strings ship with leaked geocoder tokens
// like "US GUEST HOME" between the city and state. They also arrive in two
// shapes: underscore-tokenized (I_40_W) and space-separated raw strings
// ("PHOENIX ARIZONA US GUEST HOME CORRIDOR" or full street addresses).
// Goals:
//   - Strip the leaked tokens (GUEST/HOME, paired US, standalone USA).
//   - When a state is recognized, rebuild as "City, ST [Corridor]".
//   - Preserve original casing for compound highway labels like
//     "US-287 N and I-40 W" so "and" doesn't become "And".
export function formatCorridorLabel(raw) {
  if (raw == null || raw === '') return '—';
  const s = String(raw).trim();
  if (!s) return '—';

  // The real API ships waypoint labels with MIXED separators (some underscores,
  // some literal spaces), e.g. "12567_n 154th ave surprise az 85379 usa_corridor".
  // Split on every plausible separator so each word lands in its own token.
  const hasUnderscores = /_/.test(s);
  let parts = s.split(/[\s_,()]+/).filter(Boolean);

  // Drop the leaked geocoder noise.
  const upper = parts.map((p) => p.toUpperCase());
  const hasGuestHome = upper.includes('GUEST') && upper.includes('HOME');
  parts = parts.filter((p) => {
    const u = p.toUpperCase();
    if (u === 'USA') return false;
    if (hasGuestHome && (u === 'GUEST' || u === 'HOME' || u === 'US')) return false;
    return true;
  });

  // For underscore-tokenized strings we title-case every part (machine input).
  // Space-separated strings keep original casing for non-state words.
  const formatted = hasUnderscores ? parts.map(titleCasePart) : [...parts];

  const tail = formatted[formatted.length - 1];
  const isCorridor = tail && /^Corridor$/i.test(tail);
  const body = isCorridor ? formatted.slice(0, -1) : formatted;

  // Find a US state (1-3 word match) anywhere in the body, scanning from the
  // end so address-style strings like "... AZ 85379" still anchor on AZ.
  let stateAbbr = null;
  let stateIdx = -1;
  outer: for (let i = body.length - 1; i >= 0; i--) {
    for (let take = 1; take <= 3 && i - take + 1 >= 0; take++) {
      const candidate = body.slice(i - take + 1, i + 1).join(' ');
      const abbr = usStateAbbreviation(candidate);
      if (abbr) {
        stateAbbr = abbr;
        stateIdx = i - take + 1;
        break outer;
      }
    }
  }

  let out;
  if (stateAbbr && stateIdx > 0) {
    const beforeState = body.slice(0, stateIdx);
    // Detect a street suffix inside the pre-state words so we can split
    // "12567 N 154TH AVE SURPRISE" → "12567 N 154th Ave" + "Surprise".
    let streetEndIdx = -1;
    for (let i = beforeState.length - 1; i >= 0; i--) {
      const cleaned = beforeState[i].toUpperCase().replace(/[.,]/g, '');
      if (STREET_SUFFIXES.has(cleaned)) {
        streetEndIdx = i;
        break;
      }
    }
    const tailWord = isCorridor ? ' Corridor' : '';
    // Per markup-comment #57, waypoint cards must use one consistent label
    // format. We standardize on "City, State [Corridor]" — when the raw label
    // ships a full street address, the street segment is dropped so cards
    // don't mix "Phoenix, Arizona Corridor" with "14428 W Carlin Dr, Surprise,
    // Arizona Corridor". The state is spelled out in full (markup #85
    // follow-up) to match the origin labels ("Phoenix, Arizona, US") instead
    // of the old two-letter abbreviation.
    const cityWords = streetEndIdx >= 0 && streetEndIdx < beforeState.length - 1
      ? beforeState.slice(streetEndIdx + 1)
      : beforeState;
    const city = cityWords.map(smartCase).join(' ');
    const stateName = normalizeUsState(stateAbbr) || stateAbbr;
    out = `${city}, ${stateName}${tailWord}`;
  } else {
    out = formatted.map(smartCase).join(' ');
  }

  out = out.replace(/\s+/g, ' ').trim();
  return out || '—';
}

/**
 * Card title for a feeder-corridor waypoint on the Gas Price Summary.
 *
 * The backend sends display-ready city labels (`St Joseph, MO`, `Regina, SK`), which
 * `formatCorridorLabel` would mangle (`Joseph, Missouri`, `Regina Sk`) because it treats
 * the leading `St` as a street suffix. So a label is only taken as-is when it positively
 * looks like `<city>, <2-letter region>`: exactly one comma, no digit, and exactly two
 * letters after the comma. A US state code is spelled out; any other region code
 * (a Canadian province) is kept as sent.
 *
 * EVERYTHING else — digits, `guest home`, `_corridor`, several commas, legacy single
 * tokens like `phoenix`, and a city part that reads like a street (a leading compass
 * letter, or a street suffix after the first word: "W Valentine St Surprise, AZ") — goes
 * through `formatCorridorLabel` unchanged. A city that merely contains a suffix word
 * ("Port St Lucie, FL") takes the same fallback and renders as it did before. That keeps its
 * street/ZIP stripping as a backstop, so a regression that sends an address to this
 * field still cannot put one on a card.
 *
 * @param {string|null|undefined} raw `display_label` (or legacy `waypoint_label`).
 * @returns {string} The card title, or "—" when there is no label.
 */
export function formatFuelWaypointLabel(raw) {
  if (raw == null || raw === '') return '—';
  const s = String(raw).trim();
  if (!s) return '—';
  const parts = s.split(',');
  if (parts.length === 2 && !/\d/.test(s)) {
    const city = parts[0].trim();
    const code = parts[1].trim();
    const words = city.split(/\s+/);
    // "W Valentine St Surprise": a lone compass letter first, or a street suffix after the
    // first word ("St Joseph" / "St. Louis" start with one legitimately).
    const streetish = /^[NSEW]$/i.test(words[0])
      || words.slice(1).some((w) => STREET_SUFFIXES.has(w.toUpperCase().replace(/[.,]/g, '')));
    if (city && !streetish && /^[A-Za-z]{2}$/.test(code)) {
      return `${city}, ${normalizeUsState(code) || code.toUpperCase()}`;
    }
  }
  return formatCorridorLabel(s);
}
